import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { toWorkspaceAgentContextSourceFieldValueMentionItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/toWorkspaceAgentContextSourceFieldValueMentionItem';
import {
  type WorkspaceAgentBrandKitOption,
  type WorkspaceAgentBrandKitSearchResult,
  type WorkspaceAgentMentionRowItem,
  type WorkspaceAgentMentionSearchParams,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { useWorkspaceAgentMentionPickerStore } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/useWorkspaceAgentMentionPickerStore';
import {
  getWorkspaceAgentMentionMatchStrength,
  getWorkspaceAgentMentionSourceToFollow,
  WORKSPACE_AGENT_MENTION_MATCH_STRENGTH_RANK,
  type WorkspaceAgentMentionFollowHitsResult,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';
import {
  getPrivateContextSourcesGetKey,
  getPrivateContextSourcesGetOptions,
  type PrivateContextSourcesGetResult,
} from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';
import { usePrivateContextSourcesList } from '~/components/v2/hooks/queries/usePrivateContextSourcesList';

const EMPTY_ITEMS: WorkspaceAgentMentionRowItem[] = [];

const EMPTY_BRAND_KITS: readonly WorkspaceAgentBrandKitOption[] = [];

const EMPTY_KIT_FOLLOW_HITS: Record<string, WorkspaceAgentMentionFollowHitsResult> = {};

/** Failed sticky kit: settled empty, so a healthier kit with a strictly stronger hit can win. */
const FAILED_STICKY_FOLLOW_HITS: WorkspaceAgentMentionFollowHitsResult = {
  hasLooked: true,
  isError: false,
  matchStrength: 'none',
};

const EMPTY_KIT_GET_QUERIES: ReturnType<typeof getPrivateContextSourcesGetOptions>[] = [];

type IsWaitingOnBrandKitListParams = {
  isListError: boolean;
  isListFetched: boolean;
  isSourceEnabled: boolean;
};

/**
 * `ContextSources.get` cannot run until the list names a kit. A disabled get looks settled-empty
 * (`isLoading: false`, no items), which would let a multi-word `@` query auto-close before kit
 * images are searched. Waiting is only the list still unanswered — an empty list is a real settle.
 */
export const isWaitingOnBrandKitList = ({
  isListError,
  isListFetched,
  isSourceEnabled,
}: IsWaitingOnBrandKitListParams) => isSourceEnabled && !isListFetched && !isListError;

export type ToBrandKitMentionRowsParams = {
  brandKit: Pick<PrivateContextSourcesGetResult, 'fields' | 'id'>;
  query: string;
};

/** One kit's image values, filtered in memory — there is no server search over kit files. */
export const toBrandKitMentionRows = ({
  brandKit,
  query,
}: ToBrandKitMentionRowsParams): WorkspaceAgentMentionRowItem[] => {
  const rows = brandKit.fields.flatMap((field) =>
    field.values.flatMap(
      (value) =>
        toWorkspaceAgentContextSourceFieldValueMentionItem({ contextSourceId: brandKit.id, field, value }) ?? [],
    ),
  );
  const search = query.trim().toLowerCase();

  return search ? rows.filter((row) => row.title.toLowerCase().includes(search)) : rows;
};

export type GetSelectedBrandKitIdForQueryParams = {
  isKitPinned: boolean;
  kitIds: readonly string[];
  query: string;
  resultsByKit: Record<string, WorkspaceAgentMentionFollowHitsResult>;
  stickyBrandKitId: string | null;
};

/**
 * Same strictly-stronger rule as source follow-the-hits, over kit ids. A menu pick pins this
 * mention; an auto-switch never writes the remembered kit, so the next `@` opens on the sticky one.
 * A failed sticky kit is treated as empty for that scan — source follow stays on a failed tab;
 * kit follow should still land on a healthier kit that already has a stronger hit.
 */
export const getSelectedBrandKitIdForQuery = ({
  isKitPinned,
  kitIds,
  query,
  resultsByKit,
  stickyBrandKitId,
}: GetSelectedBrandKitIdForQueryParams): string | null => {
  if (!stickyBrandKitId) {
    return null;
  }

  if (isKitPinned || query.trim() === '') {
    return stickyBrandKitId;
  }

  const stickyResult = resultsByKit[stickyBrandKitId];
  const resultsForFollow = stickyResult?.isError
    ? { ...resultsByKit, [stickyBrandKitId]: FAILED_STICKY_FOLLOW_HITS }
    : resultsByKit;

  return getWorkspaceAgentMentionSourceToFollow(stickyBrandKitId, resultsForFollow, kitIds) ?? stickyBrandKitId;
};

/** One `ContextSources.get` answers for every query, so this source has no pages to ask for. */
const NOOP = () => undefined;

export type UsePrivateWorkspaceAgentBrandKitMentionSearchParams = Omit<
  WorkspaceAgentMentionSearchParams,
  'workspaceId'
> & {
  /**
   * The kit list decides whether the Brand kit chip renders at all, so it loads for as long as the
   * picker is open rather than only while this source is the active one.
   */
  isPickerOpen: boolean;
  /**
   * Required, unlike the sibling sources: which kit is selected is remembered per workspace, so a
   * missing one would not degrade the tab, it would silently unhook the preference from it.
   */
  workspaceId: string;
};

/**
 * Every kit's image values, fetched in parallel while the picker is open. The tab still shows one
 * kit at a time; a typed query that hits another kit harder than the sticky one moves the selection
 * for this mention only. There is no server-side search over kit values, so each kit is fetched once
 * and the query filters it in memory — a kit is a handful of files, not a library, which is also why
 * this source has no pagination and never goes stale behind its input.
 */
export const usePrivateWorkspaceAgentBrandKitMentionSearch = ({
  enabled,
  isPickerOpen,
  query,
  workspaceId,
}: UsePrivateWorkspaceAgentBrandKitMentionSearchParams): WorkspaceAgentBrandKitSearchResult => {
  // The panel session always carries a workspace, so an absent one is a broken invariant rather than
  // a state the picker has a design for. Guarded once here and reported, which is what lets the kit
  // preference below read and write one id instead of forking every site on the same question.
  useEffect(() => {
    if (!workspaceId) {
      reportErrorToBugsnag({
        context: 'usePrivateWorkspaceAgentBrandKitMentionSearch',
        error: new Error('The workspace agent mention picker opened without a workspace'),
      });
    }
  }, [workspaceId]);

  const queryClient = useQueryClient();
  const storedBrandKitId = useWorkspaceAgentMentionPickerStore(
    (state) => state.selectedBrandKitIdByWorkspaceId[workspaceId],
  );
  const selectBrandKitInStore = useWorkspaceAgentMentionPickerStore((state) => state.selectBrandKit);
  const [isKitPinned, setIsKitPinned] = useState(false);

  const {
    data: brandKitList,
    isError: isListError,
    isFetched: isListFetched,
  } = usePrivateContextSourcesList({ workspaceId }, { enabled: isPickerOpen });

  const brandKits = useMemo<readonly WorkspaceAgentBrandKitOption[]>(
    () => brandKitList?.data.map(({ id, name }) => ({ id, name })) ?? EMPTY_BRAND_KITS,
    [brandKitList],
  );

  useEffect(() => {
    if (!isPickerOpen) {
      setIsKitPinned(false);
    }
  }, [isPickerOpen]);

  const isWaitingOnList = isWaitingOnBrandKitList({
    isListError,
    isListFetched,
    isSourceEnabled: enabled,
  });

  const kitGetQueries = useMemo(
    () =>
      brandKits.length === 0
        ? EMPTY_KIT_GET_QUERIES
        : brandKits.map((kit) =>
            getPrivateContextSourcesGetOptions({ contextSourceId: kit.id, workspaceId }, { enabled: isPickerOpen }),
          ),
    [brandKits, isPickerOpen, workspaceId],
  );

  const kitQueryResults = useQueries({ queries: kitGetQueries });
  // `useQueries` returns a new array each render; keep the last snapshot whose data actually changed.
  const kitQuerySignature = kitQueryResults
    .map(
      (result) =>
        `${result.dataUpdatedAt}:${Number(result.isFetched)}:${Number(result.isError)}:${Number(result.isLoading)}:${Number(result.isFetching)}`,
    )
    .join('|');
  const stableKitQueryResultsRef = useRef(kitQueryResults);
  const kitQuerySignatureRef = useRef(kitQuerySignature);

  if (kitQuerySignatureRef.current !== kitQuerySignature) {
    stableKitQueryResultsRef.current = kitQueryResults;
    kitQuerySignatureRef.current = kitQuerySignature;
  }
  const stableKitQueryResults = stableKitQueryResultsRef.current;

  const kitIds = useMemo(() => brandKits.map((kit) => kit.id), [brandKits]);

  const { hasLooked, isErrorByKitId, isFetching, isGetLoading, itemsByKitId, resultsByKit } = useMemo(() => {
    const results = stableKitQueryResults;
    const nextItemsByKitId = new Map<string, WorkspaceAgentMentionRowItem[]>();
    const nextResultsByKit: Record<string, WorkspaceAgentMentionFollowHitsResult> = {};
    const nextIsErrorByKitId: Record<string, boolean> = {};

    brandKits.forEach((kit, index) => {
      const result = results[index];
      const rows = result?.data ? toBrandKitMentionRows({ brandKit: result.data, query }) : EMPTY_ITEMS;

      nextItemsByKitId.set(kit.id, rows);
      nextIsErrorByKitId[kit.id] = result?.isError ?? false;
      nextResultsByKit[kit.id] = {
        hasLooked: result?.isFetched ?? false,
        isError: result?.isError ?? false,
        matchStrength: getWorkspaceAgentMentionMatchStrength(rows, query),
      };
    });

    return {
      hasLooked: isListFetched && (brandKits.length === 0 || results.every((result) => result.isFetched)),
      isErrorByKitId: nextIsErrorByKitId,
      isFetching: results.some((result) => result.isFetching),
      isGetLoading: results.some((result) => result.isLoading),
      itemsByKitId: nextItemsByKitId,
      resultsByKit: brandKits.length > 0 ? nextResultsByKit : EMPTY_KIT_FOLLOW_HITS,
    };
  }, [brandKits, isListFetched, query, stableKitQueryResults]);

  // A remembered kit that has since been deleted must not strand the picker on a tab that can never
  // answer, so the stored id only holds while the list still names it.
  const stickyBrandKitId = brandKits.find((kit) => kit.id === storedBrandKitId)?.id ?? brandKits[0]?.id ?? null;
  const selectedBrandKitId = getSelectedBrandKitIdForQuery({
    isKitPinned,
    kitIds,
    query,
    resultsByKit,
    stickyBrandKitId,
  });
  const items = selectedBrandKitId ? (itemsByKitId.get(selectedBrandKitId) ?? EMPTY_ITEMS) : EMPTY_ITEMS;
  const isError = selectedBrandKitId ? (isErrorByKitId[selectedBrandKitId] ?? false) : false;

  const matchStrength = useMemo(() => {
    let strongest = getWorkspaceAgentMentionMatchStrength(EMPTY_ITEMS, query);

    kitIds.forEach((kitId) => {
      const strength = resultsByKit[kitId]?.matchStrength ?? strongest;

      if (
        WORKSPACE_AGENT_MENTION_MATCH_STRENGTH_RANK[strength] > WORKSPACE_AGENT_MENTION_MATCH_STRENGTH_RANK[strongest]
      ) {
        strongest = strength;
      }
    });

    return strongest;
  }, [kitIds, query, resultsByKit]);

  const selectBrandKit = useCallback(
    (brandKitId: string) => {
      setIsKitPinned(true);
      selectBrandKitInStore(workspaceId, brandKitId);
    },
    [selectBrandKitInStore, workspaceId],
  );

  const retryMentionSearch = useCallback(() => {
    brandKits.forEach((kit) => {
      queryClient
        .invalidateQueries({
          queryKey: getPrivateContextSourcesGetKey({ contextSourceId: kit.id, workspaceId }),
        })
        .catch((error: unknown) => {
          reportErrorToBugsnag({
            context: 'usePrivateWorkspaceAgentBrandKitMentionSearch',
            error,
          });
        });
    });
  }, [brandKits, queryClient, workspaceId]);

  // A fresh literal each render would re-render the popup on every keystroke.
  return useMemo(
    () => ({
      brandKits,
      fetchNextPage: NOOP,
      hasNextPage: false,
      // Every kit has answered; the filter over them is synchronous from there.
      hasLooked,
      isError,
      isFetching,
      isLoading: isGetLoading || isWaitingOnList,
      // An in-memory filter has nothing to lag behind: these rows are always this query's.
      isStale: false,
      items,
      matchStrength,
      refetch: retryMentionSearch,
      selectBrandKit,
      selectedBrandKitId,
    }),
    [
      brandKits,
      hasLooked,
      isError,
      isFetching,
      isGetLoading,
      isWaitingOnList,
      items,
      matchStrength,
      retryMentionSearch,
      selectBrandKit,
      selectedBrandKitId,
    ],
  );
};
